import type React from 'react';
import { isPast } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { getStatusTransitions, normalizeTaskState } from '@/components/tasks/constants';
import { cn } from '@/lib/utils';
import { toLocalDate } from '@/utils/temporal';

interface InlineDeadlineStatusProps {
  state?: string | null;
  createdBy?: string | number | null;
  date: Date | string;
  onMoveToNeedsConfirmation: () => void;
  onConfirm: () => void;
  onComplete: () => void;
}

type SingleStatus = { label: string; className: string; dotClassName: string };

// Collapses the several possible statuses of a deadline into the single most
// important one to surface. Only one is ever shown, in priority order:
//   1. Needs Confirmation (auto-extracted, not yet confirmed)
//   2. Overdue (past its date and not completed)
//   3. Completed
// "Confirmed" is intentionally not surfaced — it's the passive baseline, so it
// renders as nothing (just a faint dot to open the menu).
function resolveSingleStatus(state: string, date: Date | string): SingleStatus | null {
  const parsed = toLocalDate(date);
  const isOverdue = !Number.isNaN(parsed.getTime()) && isPast(parsed) && state !== 'Completed';

  if (state === 'Extracted') {
    return { label: 'Needs confirmation', className: 'text-stone-400', dotClassName: 'bg-amber-400/80' };
  }
  if (isOverdue) return { label: 'Overdue', className: 'text-stone-400', dotClassName: 'bg-red-400' };
  if (state === 'Completed') {
    return { label: 'Completed', className: 'text-stone-400', dotClassName: 'bg-emerald-400' };
  }
  return null;
}

// Subtle, tag-free status indicator for the Table: a coloured bullet + label
// shown inline beside the date. Clicking it opens the same state-transition menu
// the standalone Status column used to host.
export const InlineDeadlineStatus: React.FC<InlineDeadlineStatusProps> = ({
  state,
  createdBy,
  date,
  onMoveToNeedsConfirmation,
  onConfirm,
  onComplete,
}) => {
  const normalized = normalizeTaskState(state);
  const single = resolveSingleStatus(normalized, date);
  const isExtracted = !createdBy;

  // Nothing worth surfacing (e.g. a plain Confirmed deadline) → render nothing
  // rather than a lone bullet. Status can still be changed from the row's side
  // panel / edit dialog.
  if (!single) return null;

  const availableStates = getStatusTransitions({
    state: normalized,
    isExtracted,
    onMoveToNeedsConfirmation,
    onConfirm,
    onComplete,
  });

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          title="Click to change status"
          className={cn(
            '-ml-0.5 inline-flex w-fit items-center gap-1.5 rounded-sm px-0.5 py-0.5 text-[11px] font-normal tracking-tight transition-colors hover:bg-gray-100',
            single.className,
          )}
        >
          <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', single.dotClassName)} />
          <span className="whitespace-nowrap">{single.label}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" onClick={(e) => e.stopPropagation()}>
        {availableStates.map((stateOption) => (
          <DropdownMenuItem key={stateOption.label} onClick={stateOption.action}>
            {stateOption.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
